import React from 'react';
import { Clock, BookOpen, MapPin, User, Trash2, MessageSquare, Users } from 'lucide-react';
import FormInput from '../FormInput';

const ExamenCreneau = ({ 
  creneau, 
  creneauIndex, 
  sessionIndex, 
  modules, 
  salles, 
  surveillants, // Remplace utilisateurs
  onCreneauChange, 
  onRemoveCreneau, 
  canRemove 
}) => {
  
  const getModuleName = () => {
    if (!creneau.module || !Array.isArray(modules)) return null;
    return modules.find(m => m.id == creneau.module)?.libelle;
  };

  const getTimeDisplay = () => {
    if (creneau.heure_debut && creneau.heure_fin) {
      return `${creneau.heure_debut} - ${creneau.heure_fin}`;
    }
    return null;
  };

  const getSalleName = () => {
    if (!creneau.salle || !Array.isArray(salles)) return null;
    return salles.find(s => s.id == creneau.salle)?.libelle;
  };

  const getSurveillantName = (surveillantId) => {
    if (!surveillantId || !Array.isArray(surveillants)) return null;
    return surveillants.find(s => s.id === surveillantId)?.label;
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-all duration-200">
      {/* Header du créneau */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-[#202654] to-[#202654]/80 rounded-lg flex items-center justify-center">
              <Clock className="h-4 w-4 text-white" />
            </div>
            <h5 className="font-semibold text-gray-800">Créneau {creneauIndex + 1}</h5>
          </div>
          
          {/* Badges informatifs */}
          <div className="flex items-center space-x-2">
            {getModuleName() && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <BookOpen className="h-3 w-3 mr-1" />
                {getModuleName()}
              </span>
            )}
            {getTimeDisplay() && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <Clock className="h-3 w-3 mr-1" />
                {getTimeDisplay()}
              </span>
            )}
            {getSalleName() && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                <MapPin className="h-3 w-3 mr-1" />
                {getSalleName()}
              </span>
            )}
          </div>
        </div>

        {/* Bouton de suppression */}
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemoveCreneau(sessionIndex, creneauIndex)}
            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            title="Supprimer ce créneau"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Formulaire du créneau */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Heure de début */}
        <div>
          <FormInput
            label="Heure de début"
            type="time"
            value={creneau.heure_debut}
            onChange={(e) => onCreneauChange(sessionIndex, creneauIndex, 'heure_debut', e.target.value)}
            required
            icon={Clock}
          />
        </div>

        {/* Heure de fin */}
        <div>
          <FormInput
            label="Heure de fin"
            type="time"
            value={creneau.heure_fin}
            onChange={(e) => onCreneauChange(sessionIndex, creneauIndex, 'heure_fin', e.target.value)}
            required
            icon={Clock}
          />
        </div>

        {/* Module */}
        <div>
          <FormInput
            label="Module / Matière"
            type="select"
            value={creneau.module}
            onChange={(e) => onCreneauChange(sessionIndex, creneauIndex, 'module', e.target.value)}
            options={[
              { value: '', label: 'Sélectionner un module' },
              ...(Array.isArray(modules) ? modules.map(module => ({
                value: module.id,
                label: module.libelle
              })) : [])
            ]}
            required
            icon={BookOpen}
          />
        </div>

        {/* Salle */}
        <div>
          <FormInput
            label="Salle d'examen"
            type="select"
            value={creneau.salle}
            onChange={(e) => onCreneauChange(sessionIndex, creneauIndex, 'salle', e.target.value)}
            options={[
              { value: '', label: 'Sélectionner une salle' },
              ...(Array.isArray(salles) ? salles.map(salle => ({
                value: salle.id,
                label: `${salle.libelle} (${salle.capacite} places)`
              })) : [])
            ]}
            icon={MapPin}
          />
        </div>

        {/* Surveillant principal */}
        <div>
          <FormInput
            label="Surveillant principal"
            type="select"
            value={creneau.surveillant_principal}
            onChange={(e) => onCreneauChange(sessionIndex, creneauIndex, 'surveillant_principal', e.target.value)}
            options={[
              { value: '', label: 'Sélectionner un surveillant' },
              ...(Array.isArray(surveillants) ? surveillants.map(surveillant => ({
                value: surveillant.id,
                label: surveillant.label
              })) : [])
            ]}
            icon={User}
          />
        </div>

        {/* Surveillant secondaire */}
        <div>
          <FormInput
            label="Surveillant secondaire"
            type="select"
            value={creneau.surveillant_secondaire}
            onChange={(e) => onCreneauChange(sessionIndex, creneauIndex, 'surveillant_secondaire', e.target.value)}
            options={[
              { value: '', label: 'Aucun surveillant secondaire' },
              ...(Array.isArray(surveillants) ? surveillants.map(surveillant => ({
                value: surveillant.id,
                label: surveillant.label
              })) : [])
            ]}
            icon={Users}
          />
        </div>

        {/* Consignes - Prend toute la largeur */}
        <div className="md:col-span-2 lg:col-span-3">
          <FormInput
            label="Consignes particulières"
            type="textarea"
            value={creneau.consignes}
            onChange={(e) => onCreneauChange(sessionIndex, creneauIndex, 'consignes', e.target.value)}
            placeholder="Instructions spéciales, matériel autorisé, durée précise..."
            rows={2}
            icon={MessageSquare}
          />
        </div>
      </div>

      {/* Section informative des surveillants sélectionnés */}
      {(creneau.surveillant_principal || creneau.surveillant_secondaire) && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h6 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <Users className="h-4 w-4 mr-2" />
            Équipe de surveillance
          </h6>
          <div className="flex flex-wrap gap-2">
            {creneau.surveillant_principal && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <User className="h-3 w-3 mr-1" />
                Principal: {getSurveillantName(creneau.surveillant_principal)}
              </span>
            )}
            {creneau.surveillant_secondaire && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <Users className="h-3 w-3 mr-1" />
                Secondaire: {getSurveillantName(creneau.surveillant_secondaire)}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamenCreneau; 